import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { toEasternArabicNumerals, formatTimeLocale } from '../../utils/i18nHelpers';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import WheelPicker from '../../components/WheelPicker';
import { useNotification } from '../../contexts/NotificationContext';

function ReservationsContent() {
    const { t, i18n } = useTranslation();
    const { showAlert } = useNotification();
    const { T, S } = useTheme();
    const [activeTab, setActiveTab] = useState('list'); // 'list' | 'new'

    // Shared State
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState(null);

    // List View State
    const [bookings, setBookings] = useState([]);
    const [loadingBookings, setLoadingBookings] = useState(true);
    const [listDate, setListDate] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });

    // New Reservation View State

    const [services, setServices] = useState([]);
    const [closedDates, setClosedDates] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [dateStr, setDateStr] = useState('');
    const [slots, setSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [selectedArtistId, setSelectedArtistId] = useState(null);
    const [selectedSeatId, setSelectedSeatId] = useState(null);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');

    const [loadingSlots, setLoadingSlots] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Load Bookings for List View
    const fetchBookings = async () => {
        setLoadingBookings(true);
        try {
            const data = await api.getManagerBookings();
            setBookings(data.sort((a, b) => new Date(a.booking_date + ' ' + a.booking_time) - new Date(b.booking_date + ' ' + b.booking_time)));
        }
        catch (err) { console.error(err); }
        finally { setLoadingBookings(false); }
    };

    useEffect(() => { fetchBookings(); }, []);

    const setStatus = async (id, status) => {
        try { await api.updateManagerBooking(id, { status }); fetchBookings(); }
        catch (err) { showAlert(err.message); }
    };

    // Load Branches (for selecting context when filtering/creating a booking)
    useEffect(() => {
        api.getManagerBranches().then(data => {
            setBranches(data);
            if (data.length > 0) setSelectedBranchId(data[0].id);
        }).catch(() => { });
    }, []);

    // Load Branch specs for New Reservation
    useEffect(() => {
        if (selectedBranchId) {
            api.getBranchServices(selectedBranchId).then(setServices).catch(() => setServices([]));
            api.getBranchClosedDates(selectedBranchId).then(setClosedDates).catch(() => setClosedDates([]));
            setSelectedServices([]);

            // Auto-select today
            const today = new Date();
            const y = today.getFullYear();
            const m = String(today.getMonth() + 1).padStart(2, '0');
            const d = String(today.getDate()).padStart(2, '0');
            const val = `${y}-${m}-${d}`;
            setDateStr(val);
        }
    }, [selectedBranchId]);

    /* ── Build single date for today ── */
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const todayVal = `${y}-${m}-${d}`;

    const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';
    const closedInfo = closedDates.find(cd => cd.date === todayVal);
    const dateOption = {
        label: t('common.today'),
        value: todayVal,
        display: today.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
        day: locale === 'ar-EG' ? toEasternArabicNumerals(String(today.getDate()).padStart(2, '0')) : today.getDate(),
        month: today.toLocaleDateString(locale, { month: 'short' }),
        closed: !!closedInfo,
        reason: closedInfo?.reason
    };

    // Load slots when selection changes
    useEffect(() => {
        if (!selectedBranchId || selectedServices.length === 0 || !dateStr) {
            setSlots([]); setSelectedTime(null); setSelectedArtistId(null); setSelectedSeatId(null); return;
        }
        setLoadingSlots(true);
        setSelectedTime(null); setSelectedArtistId(null); setSelectedSeatId(null);
        const lang = i18n.language && i18n.language.startsWith('ar') ? 'ar' : null;
        api.getBranchAvailability(selectedBranchId, selectedServices, dateStr, lang)
            .then(data => {
                setSlots(data);
                const times = [...new Set(data.map(s => s.start_time))].filter(Boolean).sort();
                if (times.length > 0) setSelectedTime(times[0]);
            })
            .catch(() => setSlots([]))
            .finally(() => setLoadingSlots(false));
    }, [selectedBranchId, selectedServices, dateStr, i18n.language]);

    const toggleService = id => setSelectedServices(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const getSelectedServicesData = () => services.filter(s => selectedServices.includes(s.id));
    const getTotalDuration = () => getSelectedServicesData().reduce((sum, s) => sum + s.duration_minutes, 0);

    const formatTime = (time24) => formatTimeLocale(time24, locale);

    const timeOptions = useMemo(() => {
        const times = [...new Set(slots.map(s => s.start_time))].filter(Boolean).sort();
        return times.map(t => ({ value: t, label: formatTime(t) }));
    }, [slots, locale]);

    const artistOptions = useMemo(() => {
        if (!selectedTime) return [];
        const byArtist = {};
        slots.filter(s => s.start_time === selectedTime).forEach(s => { if (!byArtist[s.artist_id]) byArtist[s.artist_id] = s.artist_name; });
        return Object.entries(byArtist).sort((a, b) => (a[1] || '').localeCompare(b[1] || '')).map(([id, name]) => ({ value: Number(id), label: name || `Artist ${id}` }));
    }, [slots, selectedTime]);

    const seatOptions = useMemo(() => {
        if (!selectedTime || selectedArtistId == null) return [];
        const bySeat = {};
        slots.filter(s => s.start_time === selectedTime && s.artist_id === selectedArtistId).forEach(s => { if (!bySeat[s.seat_id]) bySeat[s.seat_id] = s.seat_name; });
        return Object.entries(bySeat).sort((a, b) => (a[1] || '').localeCompare(b[1] || '')).map(([id, name]) => ({ value: Number(id), label: name || `Seat ${id}` }));
    }, [slots, selectedTime, selectedArtistId]);

    const selectedSlot = useMemo(() => {
        if (!selectedTime || selectedArtistId == null || selectedSeatId == null) return null;
        return slots.find(s => s.start_time === selectedTime && s.artist_id === selectedArtistId && s.seat_id === selectedSeatId) || null;
    }, [slots, selectedTime, selectedArtistId, selectedSeatId]);

    useEffect(() => {
        if (artistOptions.length > 0 && selectedTime && (selectedArtistId == null || !artistOptions.some(o => o.value === selectedArtistId))) {
            setSelectedArtistId(artistOptions[0].value); setSelectedSeatId(null);
        }
    }, [artistOptions, selectedTime]);

    useEffect(() => {
        if (seatOptions.length > 0 && selectedArtistId != null && (selectedSeatId == null || !seatOptions.some(o => o.value === selectedSeatId))) {
            setSelectedSeatId(seatOptions[0].value);
        }
    }, [seatOptions, selectedArtistId]);

    const handleSubmit = async () => {
        if (selectedServices.length === 0 || !dateStr || !selectedSlot) {
            showAlert(t('manager.pleaseCompleteSelection')); return;
        }
        setSubmitting(true);
        try {
            await api.createManagerBooking({
                branch_id: selectedBranchId,
                service_ids: selectedServices,
                artist_id: selectedSlot.artist_id,
                seat_id: selectedSlot.seat_id,
                customer_name: customerName || t('manager.walkIn'),
                customer_phone: customerPhone,
                booking_date: dateStr,
                booking_time: selectedSlot.start_time,
            });
            showAlert(t('manager.reservationAddedSuccess'));
            // Reset form
            setCustomerName('');
            setCustomerPhone('');
            setSelectedServices([]);
            setDateStr('');
            // Optional: immediately set to today again instead of clearing
            setDateStr(todayVal);
            // Switch back to list to see it
            setActiveTab('list');
            fetchBookings();
        } catch (err) {
            showAlert(err.message || t('manager.failedToAddReservation'));
        } finally {
            setSubmitting(false);
        }
    };

    const isFormValid = selectedServices.length > 0 && dateStr && selectedSlot;

    return (
        <AdminLayout role="manager">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
                <div>
                    <h1 style={S.pageTitle}>{t('admin.reservations')}</h1>
                    <div style={{ width: '40px', height: '2px', background: T.orange }} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', background: T.bgCard2, padding: '0.3rem', borderRadius: '6px', border: `1px solid ${T.border}` }}>
                    <button
                        onClick={() => setActiveTab('list')}
                        style={{ ...S.btnOutline, border: 'none', background: activeTab === 'list' ? T.orange : 'transparent', color: activeTab === 'list' ? '#fff' : T.muted, padding: '0.5rem 1rem' }}
                    >
                        {t('manager.agendaView')}
                    </button>
                    <button
                        onClick={() => setActiveTab('new')}
                        style={{ ...S.btnOutline, border: 'none', background: activeTab === 'new' ? T.orange : 'transparent', color: activeTab === 'new' ? '#fff' : T.muted, padding: '0.5rem 1rem' }}
                    >
                        {t('manager.addWalkIn')}
                    </button>
                </div>
            </div>

            {activeTab === 'list' && (
                <>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        {branches.length > 1 && (
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>{t('manager.filterByBranch')}</label>
                                <select style={S.select} value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value))}>
                                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>{t('manager.selectDate')}</label>
                            <input
                                type="date"
                                style={S.input}
                                value={listDate}
                                onChange={e => setListDate(e.target.value)}
                            />
                        </div>
                    </div>

                    {loadingBookings ? <p style={{ color: T.muted }}>{t('common.loading')}</p>
                        : bookings.length === 0 ? <p style={{ color: T.muted }}>{t('manager.noReservations')}</p>
                            : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                                    {bookings
                                        .filter(b => b.booking_date === listDate && (!selectedBranchId || b.branch_id === selectedBranchId))
                                        .map(b => (
                                            <div key={b.id} style={{ ...S.card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', borderLeft: b.user_id ? `4px solid ${T.orange}` : `4px solid ${T.greenText}` }}>
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                                                        <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', borderRadius: '3px', background: b.user_id ? (T.orange === '#ff00aa' ? 'rgba(255,0,170,0.1)' : 'rgba(27,213,173,0.1)') : 'rgba(74,222,128,0.1)', color: b.user_id ? T.orange : T.greenText, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                                                            {b.user_id ? t('manager.appBooking') : t('manager.walkIn')}
                                                        </span>
                                                        <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.white }}>
                                                            {b.service_name}
                                                        </h3>
                                                    </div>
                                                    <p style={{ fontSize: '0.85rem', color: T.white, marginBottom: '0.2rem' }}>👤 {b.user_id ? t('manager.onlineUser') : (b.customer_name || t('manager.walkIn'))} {b.customer_phone ? `- ${b.customer_phone}` : ''}</p>
                                                    <p style={{ fontSize: '0.82rem', color: T.muted }}>📅 {locale === 'ar-EG' ? toEasternArabicNumerals(b.booking_date) : b.booking_date} {locale === 'ar-EG' ? '–' : 'at'} {formatTimeLocale(b.booking_time, locale)} ({locale === 'ar-EG' ? toEasternArabicNumerals(String(b.total_duration_minutes || 0)) : (b.total_duration_minutes || 0)} min)</p>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem', color: T.text, marginRight: '1rem' }}>{locale === 'ar-EG' ? toEasternArabicNumerals(String(b.total_price)) : b.total_price} EGP</span>
                                                    <span style={S.badge(b.status)}>{b.status}</span>
                                                    {b.status !== 'confirmed' && (
                                                        <button style={S.btnSmall} onClick={() => setStatus(b.id, 'confirmed')}
                                                            onMouseEnter={e => e.target.style.color = T.greenText}
                                                            onMouseLeave={e => e.target.style.color = T.muted}>{t('admin.confirm')}</button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    {bookings.filter(b => b.booking_date === listDate && (!selectedBranchId || b.branch_id === selectedBranchId)).length === 0 && (
                                        <p style={{ color: T.muted }}>{t('manager.noReservationsOnDate')}</p>
                                    )}
                                </div>
                            )}
                </>
            )}

            {activeTab === 'new' && (
                <div style={S.card}>
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '1.5rem', color: T.white, marginBottom: '1.5rem', letterSpacing: '0.05em' }}>{t('manager.createManualReservation')}</h2>

                    {branches.length > 1 && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>{t('manager.selectBranch')}</label>
                            <select style={S.select} value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value))}>
                                <option value="">{t('manager.selectABranch')}</option>
                                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem' }}>

                        {/* Step 1: Customer Info */}
                        <div>
                            <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: T.orange, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>{t('manager.step1CustomerDetails')}</h3>
                            <input
                                placeholder={t('manager.placeholderCustomerNameWalkIn')}
                                value={customerName}
                                onChange={e => setCustomerName(e.target.value)}
                                style={S.input}
                            />
                            <input
                                placeholder={t('manager.placeholderPhoneOptional')}
                                value={customerPhone}
                                onChange={e => setCustomerPhone(e.target.value)}
                                style={S.input}
                            />

                            <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: T.orange, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '2rem', marginBottom: '1rem' }}>{t('manager.step2Services')}</h3>
                            {services.length === 0 ? (
                                <p style={{ color: T.muted, fontSize: '0.85rem' }}>{t('manager.noServicesAvailable')}</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                    {services.map(service => {
                                        const selected = selectedServices.includes(service.id);
                                        return (
                                            <div
                                                key={service.id}
                                                onClick={() => toggleService(service.id)}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${selected ? T.orange : T.border}`,
                                                    borderRadius: '6px',
                                                    background: selected ? 'rgba(27,213,173,0.1)' : 'transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.75rem',
                                                }}
                                            >
                                                <input type="checkbox" checked={selected} readOnly style={{ accentColor: T.orange }} />
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ fontSize: '0.9rem', color: selected ? T.orange : T.white, fontWeight: 500 }}>{service.name}</p>
                                                    <p style={{ fontSize: '0.75rem', color: T.muted }}>{locale === 'ar-EG' ? toEasternArabicNumerals(String(service.duration_minutes)) : service.duration_minutes} min</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Step 2: Date & Slot */}
                        <div>
                            <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: T.orange, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>{t('manager.step3Date')}</h3>
                            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                                <div
                                    style={{
                                        padding: '0.6rem 0.8rem',
                                        textAlign: 'center',
                                        border: `1px solid ${T.orange}`,
                                        borderRadius: '6px',
                                        background: dateOption.closed ? 'rgba(255,255,255,0.02)' : T.orange,
                                        opacity: dateOption.closed ? 0.3 : 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    <p style={{ fontSize: '0.7rem', color: '#fff' }}>{dateOption.label}</p>
                                    <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '1.4rem', color: '#fff' }}>{dateOption.day}</p>
                                    <p style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', marginTop: '-0.2rem' }}>{dateOption.month}</p>
                                </div>
                            </div>

                            <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: T.orange, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '2rem', marginBottom: '1rem' }}>{t('manager.step4AssignmentTime')}</h3>
                            {selectedServices.length === 0 || !dateStr ? (
                                <p style={{ color: T.muted, fontSize: '0.85rem' }}>{t('manager.selectServicesAndDateFirst')}</p>
                            ) : loadingSlots ? (
                                <p style={{ color: T.muted, fontSize: '0.85rem' }}>{t('manager.loadingSchedule')}</p>
                            ) : slots.length === 0 ? (
                                <p style={{ color: T.dangerHov, fontSize: '0.85rem' }}>{t('manager.fullyBooked')}</p>
                            ) : (
                                <div style={{ display: 'flex', gap: '0.85rem' }}>
                                    {[
                                        { label: t('manager.time'), options: timeOptions, value: selectedTime, onChange: (v) => { setSelectedTime(v); setSelectedArtistId(null); setSelectedSeatId(null); }, placeholder: t('manager.noTimes') },
                                        { label: t('manager.artist'), options: artistOptions, value: selectedArtistId, onChange: (v) => { setSelectedArtistId(v); setSelectedSeatId(null); }, placeholder: selectedTime ? t('manager.noArtists') : t('manager.selectTime') },
                                        { label: t('manager.seat'), options: seatOptions, value: selectedSeatId, onChange: setSelectedSeatId, placeholder: selectedArtistId != null ? t('manager.noSeats') : t('manager.selectArtist') },
                                    ].map(({ label, options, value, onChange, placeholder }) => (
                                        <div key={label} style={{ flex: '1', minWidth: '90px' }}>
                                            <p style={{ fontSize: '0.7rem', color: T.muted, marginBottom: '0.4rem', textAlign: 'center' }}>{label}</p>
                                            <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', background: T.bgCard2 }}>
                                                <WheelPicker options={options} value={value} onChange={onChange} height={150} itemHeight={35} placeholder={placeholder} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {isFormValid && (
                                <div style={{ marginTop: '2.5rem', padding: '1.5rem', background: 'rgba(27,213,173,0.05)', borderRadius: '8px', border: `1px solid ${T.border}` }}>
                                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: T.white, marginBottom: '0.5rem' }}>{t('manager.summaryLabel')}</p>
                                    <p style={{ fontSize: '0.85rem', color: T.muted, marginBottom: '0.2rem' }}>{selectedSlot.artist_name} at {selectedSlot.seat_name}</p>
                                    <p style={{ fontSize: '0.85rem', color: T.orange }}>{formatTime(selectedSlot.start_time)} ({locale === 'ar-EG' ? toEasternArabicNumerals(String(getTotalDuration())) : getTotalDuration()} mins block)</p>

                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        style={{ ...S.btnOrange, width: '100%', marginTop: '1rem', padding: '0.75rem' }}
                                    >
                                        {submitting ? t('manager.saving') : t('manager.confirmWalkInBooking')}
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

export default function Reservations() {
    return <ReservationsContent />;
}
